import axios from 'axios';
import { objectToURLParams } from '../../../utils/utils';
import { CivicAnalyticsServiceConfig } from '../../../config/defaultConfig';
import { Resolver } from '../../../config';
import { BufferedCivicAnalyticsEvent, CivicAnalyticsEvent } from './types';
import Log from '../../../common/logging';

// Second argument is the radix, required by linting rules.
const AUTO_FLUSH_INTERVAL_MS = parseInt(process.env.REACT_APP_ANALYTICS_BUFFER_MAX_AGE_SECONDS || '10', 10) * 1000;
const MAX_BUFFER_SIZE = parseInt(process.env.REACT_APP_ANALYTICS_BUFFER_MAX_SIZE || '5', 10);

export default class CivicAnalytics {
  protected config: CivicAnalyticsServiceConfig | undefined;

  private eventBuffer: BufferedCivicAnalyticsEvent[];

  constructor() {
    const serviceConfig = Resolver.getServiceConfig();
    this.config = serviceConfig.civicAnalytics;
    this.eventBuffer = [];

    // OK to do because this class is a singleton that's never garbage collected for the duration of the browser session.
    setInterval(() => {
      this.flushAnalyticsBuffer();
    }, AUTO_FLUSH_INTERVAL_MS);
  }

  /**
   * Sends all enqueued analytics events to the backend.
   * Called at least once every X seconds or when the queue reaches Y items, whichever comes first.
   * X = REACT_APP_ANALYTICS_BUFFER_MAX_AGE_SECONDS
   * Y = REACT_APP_ANALYTICS_BUFFER_MAX_SIZE
   */
  async flushAnalyticsBuffer(): Promise<void> {
    if (this.eventBuffer.length === 0) {
      return;
    }
    Log.debug(`Flushing ${this.eventBuffer.length} analytics event(s) to backend.`, this.eventBuffer);
    const url = this.config?.endpoint;
    const source = this.config?.source;
    if (!url || !source) {
      Log.error('Analytics URL and Source must be set in config. Cannot send analytics events.');
      return;
    }
    try {
      // If the browser supports the Beacon API, use it.
      // This means the request is guaranteed to be sent even when called from window.beforeunlaod (i.e. when closing the tab).
      // Regular fetch isn't guaranteed to complete when the page is being unloaded.
      // https://developer.mozilla.org/en-US/docs/Web/API/Navigator/sendBeacon
      // https://caniuse.com/beacon

      const eventsToSend = this.eventBuffer.map(({ event, routeName, timestamp }) =>
        objectToURLParams({ ...event, routeName, source, timestamp: timestamp.toISOString() }),
      );

      if ('sendBeacon' in navigator) {
        Log.debug('Beacon API supported. Using it to send analytics events.', { eventsToSend });
        // The call is queued by the browser and sent asap, even when the page is unloaded.
        // By default this uses text/plain, and uses no-cors.
        navigator.sendBeacon(url, JSON.stringify(eventsToSend));
        Log.debug('Queued analytics call using the Beacon API.');
      } else {
        // Browser does not support the Beacon API. Use regular fetch.
        // Events may be dropped when the page is unloaded.
        // Note that this does not use no-cors, because axios does not use fetch under the hood.
        // So the analytics-proxy endpoint must have the correct allowed origins configured for this to work.
        await axios.request({
          url,
          method: 'POST',
          data: eventsToSend,
        });
        Log.debug('Sent analytics batch successfully', { eventsSent: this.eventBuffer });
      }
      this.eventBuffer = [];
    } catch (error) {
      Log.error(`Error sending to civic analytics: ${(error as Error).message}`);
    }
  }

  async enqueueAnalyticsEvent(analyticsEvent: CivicAnalyticsEvent, routeName: string): Promise<void> {
    this.eventBuffer.push({ event: analyticsEvent, routeName, timestamp: new Date() });

    if (this.eventBuffer.length >= MAX_BUFFER_SIZE) {
      Log.debug(`Analytics buffer reached max size of ${MAX_BUFFER_SIZE} events. Flushing.`);
      await this.flushAnalyticsBuffer();
    } else {
      Log.debug('Enqueued analytics event but not flushing to backend yet.', { analyticsEvent, routeName });
    }
  }
}
